import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/** Preview de 1 cupom aplicado no checkout (manual ou automático). */
export type AppliedCouponPreview = {
    id: string;
    code: string | null;
    campaignName: string;
    itemDiscount: number;
    shippingDiscount: number;
    isAutomatic: boolean;
};

/** Recusa da última tentativa de aplicar um código (CUP-CHK-04 — nunca genérica). */
export type CouponRejection = {
    reason: string;
    title: string;
    detail: string | null;
};

/** Payload enviado em delivery_address quando o cliente não salva na agenda. */
export type EphemeralDeliveryPayload = {
    label: string;
    alias: string | null;
    zip_code: string;
    street: string;
    number: string;
    complement: string | null;
    neighborhood: string;
    city: string;
    state: string;
};

export const useCheckoutStore = defineStore('client-checkout', () => {
    const addressId = ref<string | null>(null);
    /** Quando definido (sem salvar cadastro), o pedido vai com delivery_address. */
    const ephemeralDelivery = ref<EphemeralDeliveryPayload | null>(null);
    /**
     * Checkout criou endereço salvo antes do pagamento; após pedido bem-sucedido o fluxo pode oferecer renomear (label).
     * Query param finalize_address_label transporta esse id porque reset() limpa o store.
     */
    const finalizeAddressLabelId = ref<string | null>(null);

    const paymentMethodId = ref<string | null>(null);
    const paymentIntent = ref<'online' | 'on_delivery' | null>(null);
    const preferredTransactionType = ref<'cash' | 'card_terminal' | null>(null);
    const changeFor = ref<number | null>(null);
    const notes = ref('');
    const deliveryFee = ref(0);
    const estimatedMinutes = ref(30);

    /** Código digitado no campo manual (não confirma nada por si só). */
    const couponCode = ref('');
    /** Cupons já aceitos nesta sessão de checkout (manuais + automáticos). */
    const appliedCoupons = ref<AppliedCouponPreview[]>([]);
    /** Motivo específico da última tentativa recusada (null quando não há recusa pendente). */
    const couponRejection = ref<CouponRejection | null>(null);
    /** Automáticos que o cliente removeu manualmente — não devem reaparecer sozinhos. */
    const excludedAutomaticCouponIds = ref<string[]>([]);

    const couponDiscount = computed(() =>
        appliedCoupons.value.reduce((sum, c) => sum + c.itemDiscount + c.shippingDiscount, 0),
    );
    const couponShippingDiscount = computed(() =>
        appliedCoupons.value.reduce((sum, c) => sum + c.shippingDiscount, 0),
    );

    function reset(): void {
        addressId.value = null;
        ephemeralDelivery.value = null;
        finalizeAddressLabelId.value = null;
        paymentMethodId.value = null;
        paymentIntent.value = null;
        preferredTransactionType.value = null;
        changeFor.value = null;
        notes.value = '';
        deliveryFee.value = 0;
        estimatedMinutes.value = 30;
        couponCode.value = '';
        appliedCoupons.value = [];
        couponRejection.value = null;
        excludedAutomaticCouponIds.value = [];
    }

    return {
        addressId,
        ephemeralDelivery,
        finalizeAddressLabelId,
        paymentMethodId,
        paymentIntent,
        preferredTransactionType,
        changeFor,
        notes,
        deliveryFee,
        estimatedMinutes,
        couponCode,
        appliedCoupons,
        couponRejection,
        excludedAutomaticCouponIds,
        couponDiscount,
        couponShippingDiscount,
        reset,
    };
});
